import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  ColOption,
  getReorderIndexingForRowReordering,
  isRow,
  numberToLetter,
  PositionInstructions,
  positionToStartEndRowCol,
  ReorderIndexing,
  startEndRowColToCellPosition,
  Table,
  TableRow,
} from "@coolset/cs-ui";
import { SurveyBlocks } from "modules/MainSurveyEditor/surveyBlocks";
import { DraggableBlockOption } from "../BlockOption/DraggableBlockOption";
import { getClasses } from "shared/helpers/styleHelper";
import { LayoutContext } from "contexts/LayoutContext/LayoutContext";

export const DynamicBlocksTable = (props: DynamicTableProps) => {
  const {
    maxRows,
    maxCols,
    blocks,
    onDrop,
    onBlockClick,
    onDragStart,
    draggedBlockPosition,
    setDraggedBlockPosition,
    selectedBlockIds = [],
    selectionArea,
    isLoading,
    positionInstructions = {},
    isSavingBlockFormValues,
    handleRepositionBlocks,
    onRowSelectionChange,
  } = props;

  const { genericError } = useContext(LayoutContext);

  const [courserOverPosition, setCourserOverPosition] = useState<string | null>(null);
  const [isDroppedOnValidPosition, setIsDroppedOnValidPosition] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isDraggingRow, setIsDraggingRow] = useState<boolean>(false);

  const colIndexArr = useMemo<number[]>(() => Array.from({ length: maxCols + 1 }, (_, i) => i), [maxCols]);
  const rowIndexArr = useMemo<number[]>(() => Array.from({ length: maxRows }, (_, i) => i), [maxRows]);

  const colOptions = useMemo<ColOption[]>(() => {
    return colIndexArr.map((i) => ({
      label: i === 0 ? "" : numberToLetter(i),
      width: i === 0 ? "max-content" : "minmax(120px, 1fr)",
    }));
  }, [colIndexArr]);

  const handleDragEnd = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!isDroppedOnValidPosition) setDraggedBlockPosition(null);

      setIsDroppedOnValidPosition(false);
      setIsDraggingRow(false);
      setSelectedRows([]);
      setCourserOverPosition(null);
    },
    [isDroppedOnValidPosition, setDraggedBlockPosition]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, position: string) => {
      e.preventDefault();
      if ((isRow(position) && !isDraggingRow) || (!isRow(position) && isDraggingRow)) {
        return;
      }

      if (courserOverPosition !== position) {
        setCourserOverPosition(position);
      }
    },
    [courserOverPosition, isDraggingRow]
  );

  const handleDragLeave = useCallback(() => {
    setCourserOverPosition(null);
  }, []);

  const handleDrop = useCallback(
    (position: string) => {
      setCourserOverPosition(null);
      setIsDroppedOnValidPosition(true);
      onDrop(position);
    },
    [onDrop]
  );

  const handleDragRowStart = useCallback(
    (e: React.DragEvent, rowIndex: number) => {
      const dragElement = document.createElement('div');
      const selectedRowsElements = document.querySelectorAll(".is-selected-row");
      selectedRowsElements.forEach(element => {
        const clone = element.cloneNode(true) as HTMLElement;
        clone.classList.add("module-dragged-row");
        clone.style.setProperty("--max-cols", (maxCols - 1).toString());
        clone.firstChild?.remove();
        dragElement.appendChild(clone);
      });
      
      document.body.appendChild(dragElement);
      e.dataTransfer.setDragImage(dragElement, 0, 0);
      setTimeout(() => document.body.removeChild(dragElement), 0);

      setIsDraggingRow(true);
      if (!selectedRows.includes(rowIndex)) {
        setSelectedRows([rowIndex]);
      }
    },
    [maxCols, selectedRows]
  );

  const handleDropRow = useCallback(
    (reorderToIndex: number) => {
      const [reorderIndexing, error] = getReorderIndexingForRowReordering(positionInstructions, reorderToIndex, selectedRows);
      setIsDraggingRow(false);
      setSelectedRows([]);
      setCourserOverPosition(null);

      if (error) {
        genericError(error);
        return;
      }

      handleRepositionBlocks(reorderIndexing);
    },
    [positionInstructions, selectedRows, handleRepositionBlocks, genericError]
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent, rowIndex: number) => {
      let next: number[];
      if (e.shiftKey && selectedRows.length > 0) {
        const firstSelectedRow = selectedRows[0];
        const lastSelectedRow = selectedRows[selectedRows.length - 1];
        const start = Math.min(firstSelectedRow, rowIndex);
        const end = Math.max(lastSelectedRow, rowIndex);
        next = Array.from({ length: end - start + 1 }, (_, i) => start + i);
      } else if (e.shiftKey) {
        next = [rowIndex];
      } else if (selectedRows.length === 1 && selectedRows.includes(rowIndex)) {
        next = [];
      } else {
        next = [rowIndex];
      }

      setSelectedRows(next);
      onRowSelectionChange?.(next);
    },
    [selectedRows, onRowSelectionChange],
  );

  const rows = useMemo<TableRow[]>(() => {
    const rowsTemp: TableRow[] = [];
    rowIndexArr.forEach((i) => {
      const rowPosition = i + 1;
      rowsTemp.push({
        className: getClasses({
          "is-selected-row": selectedRows.includes(rowPosition),
          "is-drag-over": courserOverPosition === String(rowPosition),
        }),
        cells: colIndexArr.map((j) => {
          const position = `${numberToLetter(j)}${rowPosition}:${numberToLetter(j)}${rowPosition}`;

          if (j === 0) {
            return {
              content: `${rowPosition}`,
              onClick: (e) => handleRowClick(e, rowPosition),
              draggable: true,
              onDragStart: (e: React.DragEvent) => handleDragRowStart(e, rowPosition),
              onDragOver: (e: React.DragEvent) => handleDragOver(e, String(rowPosition)),
              onDragLeave: handleDragLeave,
              onDragEnd: handleDragEnd,
              onDrop: () => handleDropRow(rowPosition),
              style: { userSelect: "none" },
            };
          }

          return {
            content: undefined,
            onDrop: () => handleDrop(position),
            onDragOver: (e: React.DragEvent) => handleDragOver(e, position),
            className: getClasses({
              "is-drag-over": courserOverPosition === position,
            }),
            onDragLeave: handleDragLeave,
          };
        }),
      });
    });

    blocks.forEach((block) => {
      const position = startEndRowColToCellPosition(positionInstructions[block.id]);
      if (!position) return;

      const { rowStart, colStart, colEnd } = positionToStartEndRowCol(position, maxRows, maxCols);
      const row = rowsTemp[rowStart - 1];
      const cell = row?.cells[colStart];
      const colSpan = colEnd - colStart + 1;

      if (cell) {
        cell.content = (
          <DraggableBlockOption
            onDragStart={(_e) => onDragStart(position)}
            onDragOver={(e) => handleDragOver(e, position)}
            onDragLeave={handleDragLeave}
            onDragEnd={handleDragEnd}
            onClick={(e) => onBlockClick(e, block)}
            blockType={block.contentType}
            title={block.contentObject.title}
            isCursorOver={courserOverPosition === position}
            isDragged={position === draggedBlockPosition}
            isSelected={selectedBlockIds.includes(String(block.id))}
          />
        );
        cell.span = colSpan;

        for (let i = colStart + 1; i <= colEnd; i++) {
          row.cells[i] = undefined;
        }

        delete cell.onDrop;
        delete cell.onDragOver;
      }
    });

    return rowsTemp;
  }, [
    rowIndexArr,
    blocks,
    selectedRows,
    courserOverPosition,
    colIndexArr,
    handleDragLeave,
    handleDragEnd,
    handleRowClick,
    handleDragRowStart,
    handleDragOver,
    handleDrop,
    positionInstructions,
    maxRows,
    maxCols,
    draggedBlockPosition,
    selectedBlockIds,
    onDragStart,
    onBlockClick,
    handleDropRow,
  ]);

  return (
    <Table
      colOptions={colOptions}
      rows={rows}
      tableType="dynamicBlock"
      selectionArea={selectionArea}
      isLoading={isLoading}
      className={getClasses({ "is-opacity-layover": !!isSavingBlockFormValues })}
    />
  );
};

interface DynamicTableProps {
  maxRows: number;
  maxCols: number;
  blocks: SurveyBlocks.Block[];
  onDrop: (position: string) => Promise<void>;
  onDragStart: (position: string) => void;
  onBlockClick: (e: React.MouseEvent, block: SurveyBlocks.Block) => void;
  draggedBlockPosition: string | null;
  setDraggedBlockPosition: (position: string | null) => void;
  selectedBlockIds?: string[];
  selectionArea?: string;
  isLoading?: boolean;
  positionInstructions?: PositionInstructions;
  isSavingBlockFormValues?: boolean;
  handleRepositionBlocks: (reorderIndexing: ReorderIndexing) => void;
  onRowSelectionChange?: (rowPositions: number[]) => void;
}
